import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus, Save, Trash2, Edit3, Search, Filter, ArrowUpDown,
  Settings, CheckCircle2, XCircle, AlertTriangle, BookOpen,
  ChevronDown, ChevronUp, GraduationCap, School, RefreshCw,
  FileText, Printer, Download
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

const t = {
  fr: {
    title: "Décisions de fin d'année",
    subtitle: "Configuration des décisions conditionnées pour le conseil de classe",
    addDecision: "Ajouter une décision",
    editDecision: "Modifier la décision",
    deleteDecision: "Supprimer",
    save: "Sauvegarder",
    cancel: "Annuler",
    order: "Ordre",
    decision: "Décision",
    decisionCode: "Code",
    sourceClass: "Classe source",
    nextClass: "Classe suivante",
    allClasses: "** Toutes **",
    sameLevel: "** Même Niveau **",
    nextLevel: "** Classe Suivante **",
    active: "Actif",
    conditions: "Conditions",
    studentConditions: "Conditions sur l'élève",
    minAverage: "Moy/20 >=",
    maxAverage: "Moy/20 <",
    minAge: "Âge >=",
    maxAge: "Âge <",
    maxAbsences: "Abs. non just. <=",
    maxExclusions: "Jours exclusion <=",
    groupConditions: "Conditions sur groupes de matières",
    group1: "Groupe 1",
    group2: "Groupe 2",
    minAvgGroup: "Moy/20 >=",
    subjectConditions: "Conditions sur matières",
    subject1: "Matière 1",
    subject2: "Matière 2",
    subject3: "Matière 3",
    enrollmentConditions: "Conditions sur inscription",
    examen: "Examen",
    ignore: "** Ignorer **",
    required: "Requis",
    notRequired: "Non requis",
    language: "Langue",
    french: "Français",
    english: "Anglais",
    repeating: "Redoublant",
    newInClass: "Nouveau dans la classe",
    repeatClass: "Redouble la classe",
    tripleClass: "Triple la classe",
    situation: "Situation",
    newSchool: "Nouveau dans l'école",
    oldSchool: "Ancien dans l'école",
    ageCondition: "Âge",
    ageGeMax: "Âge >= Âge Max",
    ageLtMax: "Âge < Âge Max",
    gender: "Sexe",
    male: "Masculin",
    female: "Féminin",
    averageConditions: "Conditions sur moyennes",
    avgValidated: "Moyenne validée",
    moyGeBase: "Moy >= Base",
    moyLtBase: "Moy < Base",
    avgNull: "Moyenne nulle",
    notNull: "Moy non nulle",
    isNull: "Moy nulle",
    avgRanked: "Moyenne classée",
    ranked: "Classée",
    notRanked: "Non classée",
    thCondition: "TH Condition",
    moyGeTh: "Moy >= MoyTH",
    moyGeEnc: "Moy >= MoyEnc",
    moyGeFel: "Moy >= MoyFel",
    subjectCoeffConditions: "Conditions matières/coeffs",
    coefsValidated: "Coefs validés",
    creditsValidated: "Crédits validés",
    notesValidated: "Notes validées",
    pct100: "= 100%",
    pctGe80: ">= 80%",
    pctGe75: ">= 75%",
    pctLt80: "< 80%",
    pctLt75: "< 75%",
    notesModulated: "Notes modulées",
    atLeastOne: "Au moins une",
    none: "Aucune",
    notesFailed: "Notes échouées",
    notesEliminated: "Notes éliminées",
    notesAbsent: "Notes absentées",
    sqlCondition: "Condition SQL extra",
    thresholds: "Seuils de distinctions",
    thThreshold: "Tableau d'honneur >=",
    encThreshold: "Encouragements >=",
    felThreshold: "Félicitations >=",
    avertTravailThreshold: "Avert. travail <",
    blameTravailThreshold: "Blâme travail <",
    noData: "Aucune décision configurée",
    search: "Rechercher...",
    confirmDelete: "Supprimer cette décision ?",
    success: "Succès",
    error: "Erreur",
    loading: "Chargement...",
    print: "Imprimer",
    export: "Exporter",
    actions: "Actions",
  },
  en: {
    title: "Year-End Decisions",
    subtitle: "Configure conditional decisions for class council",
    addDecision: "Add Decision",
    editDecision: "Edit Decision",
    deleteDecision: "Delete",
    save: "Save",
    cancel: "Cancel",
    order: "Order",
    decision: "Decision",
    decisionCode: "Code",
    sourceClass: "Source Class",
    nextClass: "Next Class",
    allClasses: "** All **",
    sameLevel: "** Same Level **",
    nextLevel: "** Next Class **",
    active: "Active",
    conditions: "Conditions",
    studentConditions: "Student Conditions",
    minAverage: "Avg/20 >=",
    maxAverage: "Avg/20 <",
    minAge: "Age >=",
    maxAge: "Age <",
    maxAbsences: "Unjust. abs. <=",
    maxExclusions: "Exclusion days <=",
    groupConditions: "Subject Group Conditions",
    group1: "Group 1",
    group2: "Group 2",
    minAvgGroup: "Avg/20 >=",
    subjectConditions: "Subject Conditions",
    subject1: "Subject 1",
    subject2: "Subject 2",
    subject3: "Subject 3",
    enrollmentConditions: "Enrollment Conditions",
    examen: "Exam",
    ignore: "** Ignore **",
    required: "Required",
    notRequired: "Not required",
    language: "Language",
    french: "French",
    english: "English",
    repeating: "Repeating",
    newInClass: "New in class",
    repeatClass: "Repeating class",
    tripleClass: "Triple class",
    situation: "Situation",
    newSchool: "New in school",
    oldSchool: "Old in school",
    ageCondition: "Age",
    ageGeMax: "Age >= Max Age",
    ageLtMax: "Age < Max Age",
    gender: "Gender",
    male: "Male",
    female: "Female",
    averageConditions: "Average Conditions",
    avgValidated: "Validated avg",
    moyGeBase: "Avg >= Base",
    moyLtBase: "Avg < Base",
    avgNull: "Null avg",
    notNull: "Avg not null",
    isNull: "Avg null",
    avgRanked: "Ranked avg",
    ranked: "Ranked",
    notRanked: "Not ranked",
    thCondition: "TH Condition",
    moyGeTh: "Avg >= TH",
    moyGeEnc: "Avg >= Enc",
    moyGeFel: "Avg >= Fel",
    subjectCoeffConditions: "Subject/Coeff Conditions",
    coefsValidated: "Validated coefs",
    creditsValidated: "Validated credits",
    notesValidated: "Validated grades",
    pct100: "= 100%",
    pctGe80: ">= 80%",
    pctGe75: ">= 75%",
    pctLt80: "< 80%",
    pctLt75: "< 75%",
    notesModulated: "Modulated grades",
    atLeastOne: "At least one",
    none: "None",
    notesFailed: "Failed grades",
    notesEliminated: "Eliminated grades",
    notesAbsent: "Absent grades",
    sqlCondition: "Extra SQL Condition",
    thresholds: "Distinction Thresholds",
    thThreshold: "Honor roll >=",
    encThreshold: "Encouragements >=",
    felThreshold: "Congratulations >=",
    avertTravailThreshold: "Warning work <",
    blameTravailThreshold: "Blame work <",
    noData: "No decisions configured",
    search: "Search...",
    confirmDelete: "Delete this decision?",
    success: "Success",
    error: "Error",
    loading: "Loading...",
    print: "Print",
    export: "Export",
    actions: "Actions",
  }
};

interface Decision {
  id?: number;
  school_id: number;
  order_index: number;
  is_active: boolean;
  decision_label: string;
  decision_code: string;
  source_class_id: number | null;
  next_class_id: number | null;
  next_class_label: string | null;
  min_average: number | null;
  max_average: number | null;
  min_age: number | null;
  max_age: number | null;
  max_absences_unjustified: number | null;
  max_exclusion_days: number | null;
  group1_subjects: string | null;
  group1_min_avg: number | null;
  group2_subjects: string | null;
  group2_min_avg: number | null;
  subject1_id: number | null;
  subject1_min_avg: number | null;
  subject2_id: number | null;
  subject2_min_avg: number | null;
  subject3_id: number | null;
  subject3_min_avg: number | null;
  examen_required: string | null;
  language_filter: string | null;
  repeating_filter: string | null;
  situation_filter: string | null;
  age_filter: string | null;
  gender_filter: string | null;
  average_validated: string | null;
  average_null: string | null;
  average_ranked: string | null;
  th_condition: string | null;
  coefs_validated: string | null;
  credits_validated: string | null;
  notes_validated: string | null;
  notes_modulated: string | null;
  notes_failed: string | null;
  notes_eliminated: string | null;
  notes_absent: string | null;
  sql_condition: string | null;
  th_threshold: number | null;
  enc_threshold: number | null;
  fel_threshold: number | null;
  avert_travail_threshold: number | null;
  blame_travail_threshold: number | null;
}

const emptyDecision = (): Decision => ({
  school_id: 1,
  order_index: 0,
  is_active: true,
  decision_label: "",
  decision_code: "",
  source_class_id: null,
  next_class_id: null,
  next_class_label: null,
  min_average: null,
  max_average: null,
  min_age: null,
  max_age: null,
  max_absences_unjustified: null,
  max_exclusion_days: null,
  group1_subjects: null,
  group1_min_avg: null,
  group2_subjects: null,
  group2_min_avg: null,
  subject1_id: null,
  subject1_min_avg: null,
  subject2_id: null,
  subject2_min_avg: null,
  subject3_id: null,
  subject3_min_avg: null,
  examen_required: null,
  language_filter: null,
  repeating_filter: null,
  situation_filter: null,
  age_filter: null,
  gender_filter: null,
  average_validated: null,
  average_null: null,
  average_ranked: null,
  th_condition: null,
  coefs_validated: null,
  credits_validated: null,
  notes_validated: null,
  notes_modulated: null,
  notes_failed: null,
  notes_eliminated: null,
  notes_absent: null,
  sql_condition: null,
  th_threshold: 16,
  enc_threshold: 14,
  fel_threshold: 12,
  avert_travail_threshold: 8,
  blame_travail_threshold: 6,
});

export default function YearEndDecisionsPage() {
  const { lang } = useLang();
  const txt = t[lang];
  const { user } = useAuthStore();
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Decision | null>(null);
  const [form, setForm] = useState<Decision>(emptyDecision());
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const schoolId = user?.school_id || 1;

  const loadData = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [decRes, clsRes, subjRes] = await Promise.all([
        apiService.get("/year-end-decisions", { params: { school_id: schoolId } }),
        apiService.get("/classes", { params: { school_id: schoolId, per_page: 1000 } }),
        apiService.get("/subjects", { params: { school_id: schoolId, per_page: 1000 } }),
      ]);
      console.log("[YearEnd] decisions:", decRes);
      console.log("[YearEnd] classes:", clsRes);
      console.log("[YearEnd] subjects:", subjRes);
      // apiService.get retourne response.data directement
      const decList = (decRes as any)?.items || (decRes as any)?.data?.items || (Array.isArray(decRes) ? decRes : []);
      const clsList = Array.isArray(clsRes) ? clsRes : ((clsRes as any)?.data || (clsRes as any)?.items || []);
      const subjList = Array.isArray(subjRes) ? subjRes : ((subjRes as any)?.data || (subjRes as any)?.items || []);
      setDecisions(decList);
      setClasses(clsList);
      setSubjects(subjList);
      console.log("[YearEnd] classes loaded:", clsList.length);
      console.log("[YearEnd] subjects loaded:", subjList.length);
    } catch (e: any) {
      console.error("[YearEnd] load error:", e?.response?.data || e?.message || e);
      toast.error(txt.error);
    } finally {
      setLoading(false);
    }
  }, [schoolId, txt.error]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    if (!search) return decisions;
    const q = search.toLowerCase();
    return decisions.filter(d =>
      d.decision_label.toLowerCase().includes(q) ||
      d.decision_code.toLowerCase().includes(q)
    );
  }, [decisions, search]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyDecision(), school_id: schoolId, order_index: decisions.length + 1 });
    setShowModal(true);
  };

  const openEdit = (d: Decision) => {
    setEditing(d);
    setForm({ ...d });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing?.id) {
        await apiService.put(`/year-end-decisions/${editing.id}`, form);
        toast.success(txt.success);
      } else {
        await apiService.post("/year-end-decisions", form);
        toast.success(txt.success);
      }
      setShowModal(false);
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(txt.confirmDelete)) return;
    try {
      await apiService.delete(`/year-end-decisions/${id}`);
      toast.success(txt.success);
      loadData();
    } catch (e) {
      toast.error(txt.error);
    }
  };

  const updateForm = (key: keyof Decision, value: any) => {
    setForm(prev => ({ ...prev, [key]: value === "" ? null : value }));
  };

  const renderSelect = (
    label: string,
    value: string | null,
    onChange: (v: string | null) => void,
    options: { value: string | null; label: string }[]
  ) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
      >
        {options.map(o => (
          <option key={o.value || "__null"} value={o.value || ""}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const renderNumberInput = (label: string, value: number | null, onChange: (v: number | null) => void) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
      />
    </div>
  );

  const selectOptions = {
    examen: [
      {value: null as string|null, label: txt.ignore},
      {value: "required", label: txt.required},
      {value: "not_required", label: txt.notRequired}
    ],
    language: [
      {value: null as string|null, label: txt.ignore},
      {value: "francais", label: txt.french},
      {value: "anglais", label: txt.english}
    ],
    repeating: [
      {value: null as string|null, label: txt.ignore},
      {value: "new", label: txt.newInClass},
      {value: "repeat", label: txt.repeatClass},
      {value: "triple", label: txt.tripleClass}
    ],
    situation: [
      {value: null as string|null, label: txt.ignore},
      {value: "new_school", label: txt.newSchool},
      {value: "old_school", label: txt.oldSchool}
    ],
    age: [
      {value: null as string|null, label: txt.ignore},
      {value: "age_ge_max", label: txt.ageGeMax},
      {value: "age_lt_max", label: txt.ageLtMax}
    ],
    gender: [
      {value: null as string|null, label: txt.ignore},
      {value: "M", label: txt.male},
      {value: "F", label: txt.female}
    ],
    avgValidated: [
      {value: null as string|null, label: txt.ignore},
      {value: "moy_ge_base", label: txt.moyGeBase},
      {value: "moy_lt_base", label: txt.moyLtBase}
    ],
    avgNull: [
      {value: null as string|null, label: txt.ignore},
      {value: "not_null", label: txt.notNull},
      {value: "is_null", label: txt.isNull}
    ],
    avgRanked: [
      {value: null as string|null, label: txt.ignore},
      {value: "ranked", label: txt.ranked},
      {value: "not_ranked", label: txt.notRanked}
    ],
    thCondition: [
      {value: null as string|null, label: txt.ignore},
      {value: "moy_ge_th", label: txt.moyGeTh},
      {value: "moy_ge_enc", label: txt.moyGeEnc},
      {value: "moy_ge_fel", label: txt.moyGeFel}
    ],
    pct: [
      {value: null as string|null, label: txt.ignore},
      {value: "100", label: txt.pct100},
      {value: "ge80", label: txt.pctGe80},
      {value: "ge75", label: txt.pctGe75},
      {value: "lt80", label: txt.pctLt80},
      {value: "lt75", label: txt.pctLt75},
      {value: "at_least_one", label: txt.atLeastOne},
      {value: "none", label: txt.none}
    ],
    modulated: [
      {value: null as string|null, label: txt.ignore},
      {value: "at_least_one", label: txt.atLeastOne},
      {value: "none", label: txt.none}
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-[#1e3a8a]" />
          {txt.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{txt.subtitle}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <button onClick={openAdd} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2">
          <Plus className="w-4 h-4" /> {txt.addDecision}
        </button>
        <div className="h-6 w-px bg-gray-200" />
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder={txt.search} value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full focus:ring-2 focus:ring-[#1e3a8a]" />
        </div>
        <div className="flex-1" />
        <button onClick={loadData} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"><RefreshCw className="w-4 h-4" /></button>
        <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex items-center gap-2"><Printer className="w-4 h-4" /> {txt.print}</button>
        <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex items-center gap-2"><Download className="w-4 h-4" /> {txt.export}</button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16">{txt.order}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{txt.decision}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{txt.sourceClass}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{txt.nextClass}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-20">{txt.active}</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase w-32">{txt.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">{txt.loading}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-gray-300" /> {txt.noData}
                </td></tr>
              ) : (
                filtered.map(d => (
                  <React.Fragment key={d.id}>
                    <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setExpandedId(expandedId === d.id ? null : d.id!)}>
                      <td className="px-4 py-3 text-sm font-mono text-[#1e3a8a] font-bold">{d.order_index}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{d.decision_label}</div>
                        <div className="text-xs text-gray-500 font-mono">{d.decision_code}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {d.source_class_id ? classes.find(c => c.id === d.source_class_id)?.name || d.source_class_id : txt.allClasses}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {d.next_class_label || (d.next_class_id ? classes.find(c => c.id === d.next_class_id)?.name || d.next_class_id : txt.sameLevel)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {d.is_active ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <XCircle className="w-5 h-5 text-gray-300 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={e => { e.stopPropagation(); openEdit(d); }} className="p-1.5 hover:bg-blue-50 rounded-lg text-[#1e3a8a]"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={e => { e.stopPropagation(); handleDelete(d.id!); }} className="p-1.5 hover:bg-red-50 rounded-lg text-red-600"><Trash2 className="w-4 h-4" /></button>
                          <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
                            {expandedId === d.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === d.id && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-gray-50/50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                            <div className="bg-white rounded-lg border border-gray-200 p-3">
                              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1"><School className="w-3 h-3" /> {txt.studentConditions}</h4>
                              <div className="space-y-1 text-gray-600">
                                {d.min_average !== null && <p>Moy &gt;= {d.min_average}</p>}
                                {d.max_average !== null && <p>Moy &lt; {d.max_average}</p>}
                                {d.min_age !== null && <p>Age &gt;= {d.min_age}</p>}
                                {d.max_age !== null && <p>Age &lt; {d.max_age}</p>}
                                {d.max_absences_unjustified !== null && <p>Abs non just &lt;= {d.max_absences_unjustified}</p>}
                                {d.max_exclusion_days !== null && <p>Excl &lt;= {d.max_exclusion_days} jrs</p>}
                                {d.gender_filter && <p>Sexe: {d.gender_filter}</p>}
                                {d.repeating_filter && <p>Redoublant: {d.repeating_filter}</p>}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 p-3">
                              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1"><BookOpen className="w-3 h-3" /> {txt.averageConditions}</h4>
                              <div className="space-y-1 text-gray-600">
                                {d.average_validated && <p>{d.average_validated}</p>}
                                {d.average_null && <p>{d.average_null}</p>}
                                {d.average_ranked && <p>{d.average_ranked}</p>}
                                {d.th_condition && <p>{d.th_condition}</p>}
                                {d.coefs_validated && <p>Coefs: {d.coefs_validated}</p>}
                                {d.notes_failed && <p>Echouees: {d.notes_failed}</p>}
                                {d.notes_eliminated && <p>Eliminees: {d.notes_eliminated}</p>}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 p-3">
                              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1"><Settings className="w-3 h-3" /> {txt.thresholds}</h4>
                              <div className="space-y-1 text-gray-600">
                                {d.th_threshold !== null && <p>TH &gt;= {d.th_threshold}</p>}
                                {d.enc_threshold !== null && <p>Enc &gt;= {d.enc_threshold}</p>}
                                {d.fel_threshold !== null && <p>Fel &gt;= {d.fel_threshold}</p>}
                                {d.avert_travail_threshold !== null && <p>Avert. travail &lt; {d.avert_travail_threshold}</p>}
                                {d.blame_travail_threshold !== null && <p>Blame travail &lt; {d.blame_travail_threshold}</p>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-gray-900">{editing ? txt.editDecision : txt.addDecision}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><XCircle className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{txt.order}</label>
                  <input type="number" value={form.order_index} onChange={e => updateForm("order_index", parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{txt.decision} *</label>
                  <input value={form.decision_label} onChange={e => updateForm("decision_label", e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Admis, Redouble, Exclu..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{txt.decisionCode} *</label>
                  <input value={form.decision_code} onChange={e => updateForm("decision_code", e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" placeholder="ADMIS, REDOUBLE, EXCLU" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => updateForm("is_active", e.target.checked)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                  <label htmlFor="is_active" className="text-sm text-gray-700">{txt.active}</label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderSelect(txt.sourceClass, String(form.source_class_id || ""), v => updateForm("source_class_id", v ? parseInt(v) : null),
                  [{ value: "", label: txt.allClasses }, ...classes.map(c => ({ value: String(c.id), label: c.name }))])}
                <div className="grid grid-cols-2 gap-2">
                  {renderSelect(txt.nextClass, String(form.next_class_id || ""), v => updateForm("next_class_id", v ? parseInt(v) : null),
                    [{ value: "", label: txt.sameLevel }, ...classes.map(c => ({ value: String(c.id), label: c.name }))])}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{txt.nextClass} (libelle)</label>
                    <input value={form.next_class_label || ""} onChange={e => updateForm("next_class_label", e.target.value || null)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="** Classe Suivante **" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><School className="w-4 h-4 text-[#1e3a8a]" /> {txt.studentConditions}</summary>
                  <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {renderNumberInput(txt.minAverage, form.min_average, v => updateForm("min_average", v))}
                    {renderNumberInput(txt.maxAverage, form.max_average, v => updateForm("max_average", v))}
                    {renderNumberInput(txt.minAge, form.min_age, v => updateForm("min_age", v))}
                    {renderNumberInput(txt.maxAge, form.max_age, v => updateForm("max_age", v))}
                    {renderNumberInput(txt.maxAbsences, form.max_absences_unjustified, v => updateForm("max_absences_unjustified", v))}
                    {renderNumberInput(txt.maxExclusions, form.max_exclusion_days, v => updateForm("max_exclusion_days", v))}
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#1e3a8a]" /> {txt.groupConditions}</summary>
                  <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{txt.group1} (IDs JSON)</label>
                      <input value={form.group1_subjects || ""} onChange={e => updateForm("group1_subjects", e.target.value || null)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" placeholder="[1,2,3]" />
                      {renderNumberInput(txt.minAvgGroup, form.group1_min_avg, v => updateForm("group1_min_avg", v))}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{txt.group2} (IDs JSON)</label>
                      <input value={form.group2_subjects || ""} onChange={e => updateForm("group2_subjects", e.target.value || null)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" placeholder="[4,5,6]" />
                      {renderNumberInput(txt.minAvgGroup, form.group2_min_avg, v => updateForm("group2_min_avg", v))}
                    </div>
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#1e3a8a]" /> {txt.subjectConditions}</summary>
                  <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      {renderSelect(txt.subject1, String(form.subject1_id || ""), v => updateForm("subject1_id", v ? parseInt(v) : null),
                        [{ value: "", label: txt.ignore }, ...subjects.map(s => ({ value: String(s.id), label: s.label_fr || s.name }))])}
                      {renderNumberInput(txt.minAvgGroup, form.subject1_min_avg, v => updateForm("subject1_min_avg", v))}
                    </div>
                    <div>
                      {renderSelect(txt.subject2, String(form.subject2_id || ""), v => updateForm("subject2_id", v ? parseInt(v) : null),
                        [{ value: "", label: txt.ignore }, ...subjects.map(s => ({ value: String(s.id), label: s.label_fr || s.name }))])}
                      {renderNumberInput(txt.minAvgGroup, form.subject2_min_avg, v => updateForm("subject2_min_avg", v))}
                    </div>
                    <div>
                      {renderSelect(txt.subject3, String(form.subject3_id || ""), v => updateForm("subject3_id", v ? parseInt(v) : null),
                        [{ value: "", label: txt.ignore }, ...subjects.map(s => ({ value: String(s.id), label: s.label_fr || s.name }))])}
                      {renderNumberInput(txt.minAvgGroup, form.subject3_min_avg, v => updateForm("subject3_min_avg", v))}
                    </div>
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><School className="w-4 h-4 text-[#1e3a8a]" /> {txt.enrollmentConditions}</summary>
                  <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {renderSelect(txt.examen, form.examen_required, v => updateForm("examen_required", v), selectOptions.examen)}
                    {renderSelect(txt.language, form.language_filter, v => updateForm("language_filter", v), selectOptions.language)}
                    {renderSelect(txt.repeating, form.repeating_filter, v => updateForm("repeating_filter", v), selectOptions.repeating)}
                    {renderSelect(txt.situation, form.situation_filter, v => updateForm("situation_filter", v), selectOptions.situation)}
                    {renderSelect(txt.ageCondition, form.age_filter, v => updateForm("age_filter", v), selectOptions.age)}
                    {renderSelect(txt.gender, form.gender_filter, v => updateForm("gender_filter", v), selectOptions.gender)}
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><ArrowUpDown className="w-4 h-4 text-[#1e3a8a]" /> {txt.averageConditions}</summary>
                  <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {renderSelect(txt.avgValidated, form.average_validated, v => updateForm("average_validated", v), selectOptions.avgValidated)}
                    {renderSelect(txt.avgNull, form.average_null, v => updateForm("average_null", v), selectOptions.avgNull)}
                    {renderSelect(txt.avgRanked, form.average_ranked, v => updateForm("average_ranked", v), selectOptions.avgRanked)}
                    {renderSelect(txt.thCondition, form.th_condition, v => updateForm("th_condition", v), selectOptions.thCondition)}
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><Filter className="w-4 h-4 text-[#1e3a8a]" /> {txt.subjectCoeffConditions}</summary>
                  <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {renderSelect(txt.coefsValidated, form.coefs_validated, v => updateForm("coefs_validated", v), selectOptions.pct)}
                    {renderSelect(txt.creditsValidated, form.credits_validated, v => updateForm("credits_validated", v), selectOptions.pct)}
                    {renderSelect(txt.notesValidated, form.notes_validated, v => updateForm("notes_validated", v), selectOptions.pct)}
                    {renderSelect(txt.notesModulated, form.notes_modulated, v => updateForm("notes_modulated", v), selectOptions.modulated)}
                    {renderSelect(txt.notesFailed, form.notes_failed, v => updateForm("notes_failed", v), selectOptions.modulated)}
                    {renderSelect(txt.notesEliminated, form.notes_eliminated, v => updateForm("notes_eliminated", v), selectOptions.modulated)}
                    {renderSelect(txt.notesAbsent, form.notes_absent, v => updateForm("notes_absent", v), selectOptions.modulated)}
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><Settings className="w-4 h-4 text-[#1e3a8a]" /> {txt.thresholds}</summary>
                  <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {renderNumberInput(txt.thThreshold, form.th_threshold, v => updateForm("th_threshold", v))}
                    {renderNumberInput(txt.encThreshold, form.enc_threshold, v => updateForm("enc_threshold", v))}
                    {renderNumberInput(txt.felThreshold, form.fel_threshold, v => updateForm("fel_threshold", v))}
                    {renderNumberInput(txt.avertTravailThreshold, form.avert_travail_threshold, v => updateForm("avert_travail_threshold", v))}
                    {renderNumberInput(txt.blameTravailThreshold, form.blame_travail_threshold, v => updateForm("blame_travail_threshold", v))}
                  </div>
                </details>

                <details className="group bg-gray-50 rounded-lg border border-gray-200">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 flex items-center gap-2"><FileText className="w-4 h-4 text-[#1e3a8a]" /> {txt.sqlCondition}</summary>
                  <div className="px-4 pb-4">
                    <textarea value={form.sql_condition || ""} onChange={e => updateForm("sql_condition", e.target.value || null)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-xs" placeholder="@Moy >= 10 AND @Age < 18..." />
                  </div>
                </details>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">{txt.cancel}</button>
                <button onClick={handleSave} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2"><Save className="w-4 h-4" /> {txt.save}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}